import { generateAffordableParamsForUnit, hasAffordableConstraint } from '@common/util/affordable'
import { exhaustiveSwitchError } from '@common/util/exhaustiveSwitchError'
import { formatCurrency } from '@common/util/format'
import { isNullOrUndefined } from '@common/util/functions'
import { isNotNullOrUndefined } from '@common/util/isNotNullOrUndefined'
import { stringForIntegrationCommon } from '@common/util/styles'
import {
  Integration,
  AffordableConstraintType,
  PropertyFragment,
  UnitProfileWithUnitTypeAndCustomUnitGrouping,
  SsoIdentityProvider,
} from '~/graphql/generated/query.types'

export function domainForIntegration(integration: Integration | undefined | null) {
  const lookup: Record<Integration, string> = {
    appfolio: 'appfolio.com',
    buildium: 'buildium.com',
    demo: 'rentana.io',
    doorloop: 'doorloop.com',
    entrata: 'entrata.com',
    mri: 'mri.com',
    realpage: 'realpage.com',
    resman: 'myresman.com',
    yardi: 'yardi.com',
    aim: 'ioaimi.com',
  }
  if (integration) {
    return lookup[integration] ?? ''
  }
  return ''
}

// 2025-05-30(patricia): As of today, the only properties who don't have integrationInstances are JRK's,
// and they aren't using us anymore. I would like to create a placeholder IntegrationInstance for them,
// then make integrationInstanceId required on Property. After that, we could just access property.integrationInstance.name
// with no fallback.
export function getIntegrationDisplayName(property: PropertyFragment | null): string {
  const fallbackString = 'the PMS'
  return property === null ? fallbackString : (property.integrationInstance?.name ?? fallbackString)
}

export function stringForIntegration(integration: Integration | null | undefined): string {
  return stringForIntegrationCommon(integration)
}

/**
 * If there is only one integration, return the string for that integration.
 * If there are multiple integrations, return "the PMS" if `excludeThe` is false,
 * otherwise return "PMS".
 */
export function stringForIntegrationList(
  integrations: Integration[],
  {
    excludeThe = false,
  }: {
    /**
     * Whether to exclude "the" in the string.
     *
     * @default false
     */
    excludeThe?: boolean
  } = {}
): string {
  return integrations.length === 1
    ? stringForIntegration(integrations[0])
    : excludeThe
      ? 'PMS'
      : 'the PMS'
}

export function getPriceLimitWithHumanReadableDescription(
  unit: UnitProfileWithUnitTypeAndCustomUnitGrouping
): { description: string; limit: number } | null {
  if (
    isNullOrUndefined(unit.unitType) ||
    !hasAffordableConstraint(generateAffordableParamsForUnit(unit))
  ) {
    if (isNotNullOrUndefined(unit.maxNewLeasePrice)) {
      return {
        description: `A ${formatCurrency(unit.maxNewLeasePrice)} limit is applied to this unit due to rent control settings`,
        limit: unit.maxNewLeasePrice,
      }
    } else {
      return null
    }
  }

  const affordableConstraintType = unit.unitType.affordableConstraintType
  switch (affordableConstraintType) {
    case AffordableConstraintType.maxAllowedRent:
      if (isNotNullOrUndefined(unit.maxNewLeasePrice)) {
        if (isNullOrUndefined(unit.unitType.maxAllowedRent)) {
          return {
            description: `A ${formatCurrency(unit.maxNewLeasePrice)} limit is applied to this unit due to rent control settings`,
            limit: unit.maxNewLeasePrice,
          }
        }
        const limit = Math.min(unit.unitType.maxAllowedRent, unit.maxNewLeasePrice)
        return {
          description: `A ${formatCurrency(limit)} limit is applied to this unit`,
          limit,
        }
      } else {
        if (isNullOrUndefined(unit.unitType.maxAllowedRent)) {
          return null
        }
        return {
          description: `A ${formatCurrency(unit.unitType.maxAllowedRent)} limit is applied to this unit`,
          limit: unit.unitType.maxAllowedRent,
        }
      }
    case AffordableConstraintType.fixedRent:
      if (isNullOrUndefined(unit.unitType.fixedRent)) {
        return null
      }
      return {
        description: `This unit has a fixed rent of ${formatCurrency(unit.unitType.fixedRent)}`,
        limit: unit.unitType.fixedRent,
      }
    case AffordableConstraintType.none:
      // Handled by the early return above (no affordable rent constraint)
      return null
    default:
      throw exhaustiveSwitchError(affordableConstraintType)
  }
}

export function stringForSSOIdentityProvider(identityProvider: SsoIdentityProvider): string {
  switch (identityProvider) {
    case SsoIdentityProvider.oktaOidc:
      return 'Okta (OIDC)'
    case SsoIdentityProvider.oktaSaml:
      return 'Okta (SAML)'
    default:
      throw exhaustiveSwitchError(identityProvider)
  }
}
